import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import GalleryGrid from '../components/gallery/GalleryGrid';
import GalleryFilters from '../components/gallery/GalleryFilters';
import GalleryDownloadForm from '../components/gallery/GalleryDownloadForm';
import ActiveImageDownloads from '../components/gallery/ActiveImageDownloads';
import ImageViewer from '../components/gallery/ImageViewer';
import '../styles/Gallery.css';

function GalleryDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [filters, setFilters] = useState({
    artist: '',
    source_site: '',
    tag: '',
    color: '',
    rating: '',
    nsfw: user?.nsfw_preference || 'blur',
    search: ''
  });
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [storageInfo, setStorageInfo] = useState({
    used: 0,
    videos: 0,
    images: 0,
    limit: 5 * 1024 * 1024 * 1024
  });

  useEffect(() => {
    fetchImages();
    fetchStorageStats();
    if (user) {
      setStorageInfo(prev => ({
        ...prev,
        limit: user.storage_limit_mb * 1024 * 1024
      }));
    }
  }, [user, filters]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.artist) params.append('artist', filters.artist);
      if (filters.source_site) params.append('source_site', filters.source_site);
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.color) params.append('color', filters.color);
      if (filters.rating) params.append('rating', filters.rating);
      if (filters.nsfw) params.append('nsfw', filters.nsfw);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`/api/images?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }
    } catch (err) {
      console.error('Failed to fetch images:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageStats = async () => {
    try {
      const response = await fetch('/api/images/stats/storage', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStorageInfo({
          used: data.total.size,
          videos: data.videos.size,
          images: data.images.size,
          limit: user?.storage_limit_mb * 1024 * 1024 || 5 * 1024 * 1024 * 1024
        });
      }
    } catch (err) {
      console.error('Failed to fetch storage stats:', err);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const handleNavigate = (direction) => {
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < images.length) {
      setCurrentIndex(newIndex);
      setSelectedImage(images[newIndex]);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const storagePercent = Math.min((storageInfo.used / storageInfo.limit) * 100, 100);

  return (
    <div className="gallery-dashboard">
      <nav className="navbar">
        <div className="navbar-brand">
          <h1>GALLERY <span>DOWNLOADER</span></h1>
        </div>
        <div className="navbar-right">
          <div className="storage-indicator">
            <span>Storage</span>
            <div className="storage-bar">
              <div 
                className="bar-fill" 
                style={{ width: `${storagePercent}%` }}
              />
            </div>
            <span className="storage-details">
              {formatBytes(storageInfo.used)} / {formatBytes(storageInfo.limit)}
              <small>({formatBytes(storageInfo.videos)} videos, {formatBytes(storageInfo.images)} images)</small>
            </span>
          </div>
          <span className="user-info">{user?.username}</span>
          <div className="user-actions">
            <button className="btn-secondary" onClick={() => navigate('/')}>
              Videos
            </button>
            {user?.is_admin && (
              <button className="btn-admin" onClick={() => navigate('/x1n2t3r4n5l')}>
                Admin
              </button>
            )}
            <button className="btn-secondary" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="main-content">
        <ActiveImageDownloads onComplete={fetchImages} />

        <section className="download-section">
          <div className="section-header">
            <h2>Download Gallery</h2>
            <span className="number">01</span>
          </div>
          <GalleryDownloadForm onSuccess={fetchImages} />
        </section>

        <section className="gallery-section">
          <div className="section-header">
            <h2>Library</h2>
            <span className="number">02</span>
            <span className="image-count">{images.length} images</span>
          </div>

          <div className="gallery-layout">
            <GalleryFilters 
              filters={filters}
              onFilterChange={handleFilterChange}
              user={user}
            />
            
            <div className="gallery-content">
              {loading ? (
                <div className="loading-state">Loading...</div>
              ) : images.length === 0 ? (
                <div className="empty-state">
                  <h3>No images</h3>
                  <p>Add a gallery URL to begin</p>
                </div>
              ) : (
                <GalleryGrid 
                  images={images}
                  nsfwPreference={user?.nsfw_preference || 'blur'}
                  onImageClick={handleImageClick}
                />
              )}
            </div>
          </div>
        </section>
      </main>

      {selectedImage && (
        <ImageViewer
          image={selectedImage}
          images={images}
          currentIndex={currentIndex}
          onClose={() => setSelectedImage(null)}
          onNavigate={handleNavigate}
          nsfwPreference={user?.nsfw_preference || 'blur'}
          onDelete={fetchImages}
        />
      )}
    </div>
  );
}

export default GalleryDashboard;